import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Euro, Check, Fingerprint, Users, Calendar, AlertCircle, CreditCard } from 'lucide-react';
import { pricingData, paymentSchedule, clubData } from '../data/mock';

const PayButton = ({ url, label = 'Pagar amb targeta', testId, small = false }) => {
  if (!url) return null;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      className={`inline-flex items-center justify-center gap-2 bg-[#39FF14] hover:bg-[#2EDD0F] text-black font-bold rounded-full transition-all transform hover:scale-105 ${
        small ? 'px-4 py-1.5 text-xs' : 'w-full px-6 py-3 mt-6'
      }`}
    >
      <CreditCard className={small ? 'w-3.5 h-3.5' : 'w-5 h-5'} />
      {label}
    </a>
  );
};

export const Pricing = () => {
  return (
    <section id="pricing" className="py-20 bg-gradient-to-b from-black via-gray-900 to-black">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
            <span className="text-[#39FF14]">Quotes</span> i Preus
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Troba l'opció que millor s'adapti a les teves necessitats
          </p>
        </div>

        {/* Featured Options */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-12">
          {/* Monthly */}
          <Card className="bg-gradient-to-br from-gray-900 to-gray-800 border-2 border-gray-700 hover:border-[#39FF14] transition-all">
            <CardHeader>
              <CardTitle className="text-white text-2xl">{pricingData.monthly.description}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center mb-6">
                <div className="text-6xl font-black text-white mb-2">{pricingData.monthly.price}</div>
                <p className="text-gray-400">{pricingData.monthly.period}</p>
              </div>
              <ul className="space-y-3">
                {pricingData.monthly.includes.map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-gray-300">
                    <Check className="w-5 h-5 text-[#39FF14]" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <PayButton url={pricingData.monthly.paymentUrl} testId="pay-monthly-btn" />
            </CardContent>
          </Card>

          {/* Trimestral Primer Any - DESTACAT */}
          <Card className="bg-gradient-to-br from-[#39FF14]/20 to-gray-800 border-2 border-[#39FF14] relative overflow-hidden shadow-xl shadow-[#39FF14]/30">
            <div className="absolute top-0 right-0 bg-[#39FF14] text-black px-4 py-1 text-sm font-bold">
              RECOMANAT
            </div>
            <CardHeader>
              <CardTitle className="text-white text-2xl">{pricingData.trimestralFirstYear.description}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center mb-6">
                <div className="text-6xl font-black text-[#39FF14] mb-2">{pricingData.trimestralFirstYear.price}</div>
                <p className="text-gray-300">{pricingData.trimestralFirstYear.period}</p>
              </div>
              <ul className="space-y-3">
                {pricingData.trimestralFirstYear.includes.map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-gray-300">
                    <Check className="w-5 h-5 text-[#39FF14]" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <PayButton url={pricingData.trimestralFirstYear.paymentUrl} testId="pay-trimestral-first-year-btn" />
            </CardContent>
          </Card>
        </div>

        {/* All Options */}
        <div className="max-w-6xl mx-auto mb-12">
          <h3 className="text-3xl font-bold text-white text-center mb-8">
            Altres <span className="text-[#39FF14]">Opcions</span>
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Classes Only */}
            <Card className="bg-gray-900/50 border-2 border-gray-800 hover:border-[#39FF14] transition-all">
              <CardHeader>
                <CardTitle className="text-white text-lg">{pricingData.classesOnly.description}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-4xl font-black text-white mb-3">{pricingData.classesOnly.price}</div>
                <p className="text-gray-500 text-sm mb-3">{pricingData.classesOnly.period}</p>
                <ul className="space-y-2 text-sm">
                  {pricingData.classesOnly.includes.map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-gray-400">
                      <Check className="w-4 h-4 text-[#39FF14]" />
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-gray-600 mt-3">{pricingData.classesOnly.note}</p>
                <PayButton url={pricingData.classesOnly.paymentUrl} testId="pay-classes-only-btn" />
              </CardContent>
            </Card>

            {/* 1 Class Weekly */}
            <Card className="bg-gray-900/50 border-2 border-gray-800 hover:border-[#39FF14] transition-all">
              <CardHeader>
                <CardTitle className="text-white text-lg">{pricingData.oneClassWeekly.description}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-4xl font-black text-white mb-3">{pricingData.oneClassWeekly.price}</div>
                <p className="text-gray-500 text-sm mb-3">{pricingData.oneClassWeekly.period}</p>
                <ul className="space-y-2 text-sm">
                  {pricingData.oneClassWeekly.includes.map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-gray-400">
                      <Check className="w-4 h-4 text-[#39FF14]" />
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-gray-600 mt-3">{pricingData.oneClassWeekly.note}</p>
                <PayButton url={pricingData.oneClassWeekly.paymentUrl} testId="pay-one-class-weekly-btn" />
              </CardContent>
            </Card>

            {/* Veteran */}
            <Card className="bg-gray-900/50 border-2 border-gray-800 hover:border-[#39FF14] transition-all">
              <CardHeader>
                <CardTitle className="text-white text-lg">{pricingData.veteran.description}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-4xl font-black text-white mb-3">{pricingData.veteran.price}</div>
                <p className="text-gray-500 text-sm mb-3">{pricingData.veteran.period}</p>
                <ul className="space-y-2 text-sm">
                  {pricingData.veteran.includes.map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-gray-400">
                      <Check className="w-4 h-4 text-[#39FF14]" />
                      {item}
                    </li>
                  ))}
                </ul>
                <PayButton url={pricingData.veteran.paymentUrl} testId="pay-veteran-btn" />
              </CardContent>
            </Card>

            {/* Family 3 */}
            <Card className="bg-gradient-to-br from-[#39FF14]/10 to-gray-900 border-2 border-[#39FF14]/50 hover:border-[#39FF14] transition-all">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#39FF14]" />
                  <CardTitle className="text-white text-lg">{pricingData.family3.description}</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-4xl font-black text-[#39FF14] mb-1">{pricingData.family3.price}</div>
                <p className="text-gray-400 text-sm mb-3">{pricingData.family3.pricePerPerson}/persona · {pricingData.family3.period}</p>
                <ul className="space-y-2 text-sm">
                  {pricingData.family3.includes.map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-gray-400">
                      <Check className="w-4 h-4 text-[#39FF14]" />
                      {item}
                    </li>
                  ))}
                </ul>
                <PayButton url={pricingData.family3.paymentUrl} testId="pay-family3-btn" />
              </CardContent>
            </Card>

            {/* Family 4 */}
            <Card className="bg-gradient-to-br from-[#39FF14]/10 to-gray-900 border-2 border-[#39FF14]/50 hover:border-[#39FF14] transition-all">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#39FF14]" />
                  <CardTitle className="text-white text-lg">{pricingData.family4.description}</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-4xl font-black text-[#39FF14] mb-1">{pricingData.family4.price}</div>
                <p className="text-gray-400 text-sm mb-3">{pricingData.family4.pricePerPerson}/persona · {pricingData.family4.period}</p>
                <ul className="space-y-2 text-sm">
                  {pricingData.family4.includes.map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-gray-400">
                      <Check className="w-4 h-4 text-[#39FF14]" />
                      {item}
                    </li>
                  ))}
                </ul>
                <PayButton url={pricingData.family4.paymentUrl} testId="pay-family4-btn" />
              </CardContent>
            </Card>

            {/* Packs */}
            <Card className="bg-gray-900/50 border-2 border-gray-800 hover:border-[#39FF14] transition-all">
              <CardHeader>
                <CardTitle className="text-white text-lg">Entrades i Packs</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">{pricingData.singleEntry.description}</span>
                    <span className="text-white font-bold text-xl">{pricingData.singleEntry.price}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{pricingData.singleEntry.note}</p>
                  <div className="mt-2 flex justify-end">
                    <PayButton url={pricingData.singleEntry.paymentUrl} label="Comprar" testId="pay-single-entry-btn" small />
                  </div>
                </div>
                <div className="border-t border-gray-800 pt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">{pricingData.pack5.description}</span>
                    <span className="text-white font-bold text-xl">{pricingData.pack5.price}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{pricingData.pack5.note}</p>
                  <div className="mt-2 flex justify-end">
                    <PayButton url={pricingData.pack5.paymentUrl} label="Comprar" testId="pay-pack5-btn" small />
                  </div>
                </div>
                <div className="border-t border-gray-800 pt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">{pricingData.pack6months.description}</span>
                    <span className="text-[#39FF14] font-bold text-xl">{pricingData.pack6months.price}</span>
                  </div>
                  <div className="mt-2 flex justify-end">
                    <PayButton url={pricingData.pack6months.paymentUrl} label="Comprar" testId="pay-pack6months-btn" small />
                  </div>
                </div>
                <div className="border-t border-gray-800 pt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">{pricingData.pack12months.description}</span>
                    <span className="text-[#39FF14] font-bold text-xl">{pricingData.pack12months.price}</span>
                  </div>
                  <div className="mt-2 flex justify-end">
                    <PayButton url={pricingData.pack12months.paymentUrl} label="Comprar" testId="pay-pack12months-btn" small />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Payment Schedule */}
        <Card className="max-w-4xl mx-auto bg-gradient-to-r from-gray-900 to-black border-2 border-[#39FF14]/30">
          <CardHeader>
            <CardTitle className="text-white text-2xl flex items-center gap-3">
              <Calendar className="w-6 h-6 text-[#39FF14]" />
              {paymentSchedule.title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              {paymentSchedule.periods.map((p, i) => (
                <div key={i} className="bg-gray-800/50 rounded-lg p-4">
                  <div className="text-[#39FF14] font-bold mb-1">{p.period}</div>
                  <div className="text-gray-400 text-sm">{p.dates}</div>
                </div>
              ))}
            </div>
            <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-gray-300 text-sm">
                <span className="font-bold text-red-400">Important:</span> {paymentSchedule.latePaymentNote}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* CTA */}
        <div className="text-center mt-12">
          <a
            href="#contact"
            className="inline-flex items-center gap-2 bg-[#39FF14] hover:bg-[#2EDD0F] text-black font-bold px-8 py-4 rounded-full transition-all transform hover:scale-105"
          >
            Contacta Amb Nosaltres
          </a>
        </div>
      </div>
    </section>
  );
};
